import { useEffect } from 'react';
import { FetchClipProps, useAudioContext } from './AudioContext';

/**
 * Wrapper around the AudioContext that triggers creation of an audio element if it does not exist
 * in the context/cache.
 */
const useAudioElement = ({
  src,
  headers,
  id,
  shouldDisposeOnUnmount,
}: FetchClipProps & { shouldDisposeOnUnmount?: boolean }): [
  HTMLAudioElement | undefined,
  Error | undefined,
  ({
    src,
    id,
    shouldResetIfPaused,
  }: {
    src: string;
    shouldResetIfPaused: boolean;
    id?: string;
  }) => void
] => {
  const [
    audioContext,
    { createAudioElement, disposeAudioElementIfPaused, setGlobalAudioElement },
  ] = useAudioContext();

  const [audioElem] = audioContext[id || src] || [];

  useEffect(() => {
    createAudioElement({ src, headers, id });
    return () => {
      if (shouldDisposeOnUnmount) {
        disposeAudioElementIfPaused(src, id);
      }
    };
  }, [
    shouldDisposeOnUnmount,
    createAudioElement,
    disposeAudioElementIfPaused,
    headers,
    src,
    id,
  ]);

  if (audioElem instanceof Error) {
    return [undefined, audioElem, setGlobalAudioElement];
  }
  return [audioElem, undefined, setGlobalAudioElement];
};

export default useAudioElement;
